import { Component, OnInit, Input, OnDestroy, OnChanges, SimpleChanges, ChangeDetectorRef } from '@angular/core';
import { VRGroupService } from '../vrgroup.service';
import { MapElement } from '../../../shared/map/map-element.model';
import { Point } from 'geojson';
import { Subject, BehaviorSubject, combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';
import { Shift, TrainShift, TaskRole, ShiftPersonGroup, RoleGroupName } from '../common/train';
import { getShiftsInTrainForStation } from './personnel-utils';
import { getRoleGroupName } from '../utils/personnel.utils';

interface DisplayedPerson {
  role: TaskRole;
  fromStation: string;
  toStation: string;
  roleDesc: RoleGroupName | string;
  personId: string;
  personGroup: ShiftPersonGroup;
}

const personSortingOrder = [
  RoleGroupName.Driver, RoleGroupName.Conductor,
  RoleGroupName.AssistDriver, RoleGroupName.AssistConductor,
  RoleGroupName.Package,
] as string[];

@Component({
  selector: 'qm-train-personnel',
  templateUrl: './train-personnel.component.html',
  styleUrls: ['./train-personnel.component.scss']
})
export class TrainPersonnelComponent implements OnChanges, OnDestroy {

  @Input()
  public feature: MapElement<Point>;

  public trainShift: TrainShift = null;
  public allShifts: Shift[] = [];
  public currentPersonnelShifts: Shift[] = [];

  public showPlaceholder = true;
  public loading = true;

  private expanded = new BehaviorSubject(false);
  public expanded$ = this.expanded.asObservable();

  public currentPersonnelData = new BehaviorSubject<DisplayedPerson[]>([]);
  public filteredPersonnelData$ = combineLatest(this.currentPersonnelData, this.expanded$)
    .pipe(
      // if the view is expanded, show all current personnel, else show only the first two
      map(([data, isExpanded ]) => isExpanded ? data : data.slice(0, 2))
    );

  private ngUnsubscribe = new Subject<void>();

  constructor(private vrGroupService: VRGroupService, private cdr: ChangeDetectorRef) { }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.feature?.currentValue?.id !== changes.feature?.previousValue?.id) {
      this.expanded.next(false);
      this.loadPersonnel();
    }
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  public toggleViewOpen() {
    const previousValue = this.expanded.getValue();
    this.expanded.next(!previousValue);
  }

  private loadPersonnel() {
    // long distance and freight trains usually have personnel info available,
    // so show the loading placeholder for them to reduce UI jitter
    this.showPlaceholder =
      this.feature.properties.type === 'LONGDISTANCE' ||
      this.feature.properties.type === 'FREIGHT';

    this.loading = true;
    this.cdr.markForCheck();

    Promise.all([
      this.vrGroupService.getTrainShift(this.feature.properties.tripNumber, this.feature.properties.tripDate),
      this.vrGroupService.getTrainSchedule(this.feature.properties._id),
      this.getLastStationForTrain(this.feature.properties._id)
    ])
    .then(([trainShiftSA, latestScheduleSA, currentStation]) => {
      const trainShift = trainShiftSA.data;
      const latestSchedule = latestScheduleSA.data.data[0];
      if (!trainShift) {
        return;
      }
      this.currentPersonnelShifts = getShiftsInTrainForStation(trainShift, latestSchedule, currentStation);
      const currentPersonnel: DisplayedPerson[] = this.currentPersonnelShifts
        .filter(shift => shift.tasks.length > 0)
        .map(shift => ({
          role: shift.tasks[0].role,
          fromStation: shift.tasks[0].fromStation,
          toStation: shift.tasks[0].toStation,
          roleDesc: getRoleGroupName(shift.personGroup, shift.tasks[0].role),
          personGroup: shift.personGroup,
          personId: shift.personId
        }));
      currentPersonnel.sort((p1, p2) => personSortingOrder.indexOf(p1.roleDesc) - personSortingOrder.indexOf(p2.roleDesc));
      this.currentPersonnelData.next(currentPersonnel);
      this.loading = false;
      this.cdr.markForCheck();
    });
  }

  private async getLastStationForTrain(trainId: string) {
    const train = await this.vrGroupService.getTrainData(trainId);
    if (train.isOk) {
      const foundTrain = train.data.data[0];
      const previousOrCurrentStation = foundTrain ? foundTrain.station : undefined;
      return previousOrCurrentStation;
    }
  }
}
